import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { verifyMobileLogin } from "./../../api/api"; // 👈 use centralized API
import { Box, Typography, Button, Card, CardContent, Alert } from "@mui/material";
import Swal from "sweetalert2";

const VerifyAdmin = () => {
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [userEmail, setUserEmail] = useState("");
  const [showBranchSelection, setShowBranchSelection] = useState(false);
  const [loading, setLoading] = useState(true);

  const selectBranch = async (selectedBranch) => {
    const user = new URLSearchParams(window.location.search).get("user") || userEmail;
    
    try {
      const { getTokenForBranch } = await import("../../api/api");
      const data = await getTokenForBranch(user, selectedBranch.id);
      
      if (data.success && data.token) {
        const userData = data.user || {
          id: selectedBranch.id,
          branch_id: selectedBranch.branch_id || selectedBranch.id,
          client_id: selectedBranch.client_id,
          branch_name: selectedBranch.branch_name,
          user_name: selectedBranch.user_name,
          email: selectedBranch.email,
          phone: selectedBranch.phone,
          logo: selectedBranch.logo
        };
        
        localStorage.setItem("token", data.token);
        localStorage.setItem("user", JSON.stringify(userData));
        localStorage.setItem("branch_id", userData.branch_id || selectedBranch.id);
        localStorage.setItem("client_id", userData.client_id);
        localStorage.setItem("branch_name", userData.branch_name);
      } else {
        Swal.fire({
          icon: "error",
          title: "Access Failed",
          text: data.message || "Failed to get access. Please try again.",
        });
        return;
      }
    } catch (err) {
      console.error("Error getting token for branch:", err);
      Swal.fire({
        icon: "error",
        title: "Access Failed",
        text: "Failed to access branch. Please try again.",
      });
      return;
    }

    setTimeout(() => {
      window.location.href = "/home";
    }, 300);
  };

  useEffect(() => {
    const verify = async () => {
      console.log("Verifying Mobile Login...");
      const queryParams = new URLSearchParams(window.location.search);
      const user = queryParams.get("user"); // Get mobile number or email from URL
      const callbackToken = queryParams.get("callback_token"); // From backend ivs-callback
      const returnCode = sessionStorage.getItem("ivs_return_code"); // From login initiation

      if (!user) {
        Swal.fire({
          icon: "error",
          title: "Invalid Login",
          text: "Invalid login response. Redirecting to login page.",
        }).then(() => navigate("/login"));
        return;
      }

      if (!returnCode || !callbackToken) {
        Swal.fire({
          icon: "warning",
          title: "Invalid Verification",
          text: "Invalid verification. Please login through the login page.",
        }).then(() => navigate("/login"));
        return;
      }

      setUserEmail(user);

      try {
        // Clear stored return code after use
        sessionStorage.removeItem("ivs_return_code");
        const data = await verifyMobileLogin(user, returnCode, callbackToken);

        if (data.status) {
          console.log("Login successful!");
          
          // Check if multiple branches are available
          if (data.multiple_branches && data.available_branches) {
            setBranches(data.available_branches);
            setShowBranchSelection(true);
            setLoading(false);
          } else {
            // Single branch - proceed with login (use token from API)
            localStorage.setItem("token", data.token || data.user.id);
            localStorage.setItem("user", JSON.stringify(data.user));
            localStorage.setItem("branch_id", data.user.branch_id);
            localStorage.setItem("client_id", data.user.client_id);
            localStorage.setItem("branch_name", data.user.branch_name);

            // Redirect to dashboard
            setTimeout(() => {
              console.log("Navigating to dashboard...");
              window.location.href = "/home"; // More reliable redirect
            }, 300);
          }
        } else {
          Swal.fire({
            icon: "error",
            title: "Login Failed",
            text: data.message || "Login failed. Please try again.",
          }).then(() => navigate("/login"));
        }
      } catch (error) {
        console.error("Error verifying mobile login:", error);
        if (error.response?.status === 404) {
          Swal.fire({
            icon: "info",
            title: "No Account Found",
            text: "No account found for this email/phone. Please sign up for a free trial first.",
            confirmButtonText: "Sign Up",
          }).then(() => navigate("/free-trial"));
        } else {
          const msg = error.response?.data?.message || "Verification failed. Please try again.";
          Swal.fire({
            icon: "error",
            title: "Verification Failed",
            text: msg,
          }).then(() => navigate("/login"));
        }
      }
    };

    verify();
  }, [navigate]);

  if (loading) {
    return ;
  }

  if (showBranchSelection) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        p: 3,
        bgcolor: '#f5f5f5'
      }}>
        <Card sx={{ maxWidth: 500, width: '100%' }}>
          <CardContent>
            <Typography variant="h5" gutterBottom align="center">
              Select Branch
            </Typography>
            <Alert severity="info" sx={{ mb: 3 }}>
              Multiple branches found for {userEmail}. Please select the branch you want to access.
            </Alert>
            {branches.map((branch) => (
              <Button
                key={branch.id}
                variant="outlined"
                fullWidth
                sx={{ mb: 2, p: 2 }}
                onClick={() => selectBranch(branch)}
              >
                <Box sx={{ textAlign: 'left', width: '100%' }}>
                  <Typography variant="h6">{branch.branch_name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Username: {branch.user_name}
                  </Typography>
                </Box>
              </Button>
            ))}
          </CardContent>
        </Card>
      </Box>
    );
  }

  return ;
};

export default VerifyAdmin;